import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import {
  getDisplayableItems,
  getPrimitiveEntries,
  isArray,
  isObject,
  isPrimitive,
} from './tableValidation';

interface NodeTableProps {
  data: unknown;
  columnName?: string;
}

const headerCellSx = { fontWeight: 'bold', backgroundColor: '#e8eaf6' };

/**
 * Render a MUI table with the given headers and rows.
 */
const renderTable = (headers: string[], rows: string[][]) => (
  <Table stickyHeader>
    <TableHead>
      <TableRow>
        {headers.map((header) => (
          <TableCell key={header} style={headerCellSx}>
            {header}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.map((row, rowIndex) => (
        <TableRow key={rowIndex}>
          {row.map((cell, colIndex) => (
            <TableCell key={`${rowIndex}-${colIndex}`}>{cell}</TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

/**
 * @returns a structured table on clicking preview icon.
 */
const NodeTable = ({ data, columnName = 'Value' }: NodeTableProps) => {
  //Primitive value(key value pair): column = key name and value as single row
  if (isPrimitive(data)) {
    return renderTable([columnName], [[String(data)]]);
  }

  // ---- Single object: columns = primitive-valued keys and value as single row ----
  if (isObject(data)) {
    const entries = getPrimitiveEntries(data);
    if (entries.length === 0) return null;
    const headers = entries.map(([key]) => key);
    const row = entries.map(([, value]) => String(value));
    return renderTable(headers, [row]);
  }

  // ---- Array ----
  if (isArray(data)) {
    const displayableItems = getDisplayableItems(data as any[]);
    if (!displayableItems || displayableItems.length === 0) return null;

    if (displayableItems.every(isPrimitive)) {
      return renderTable(
        [columnName],
        displayableItems.map((item) => [String(item)]),
      );
    }

    if (displayableItems.every(isObject)) {
      const allKeys = [
        ...new Set(
          displayableItems.flatMap((item) =>
            getPrimitiveEntries(item as Record<string, unknown>).map(([k]) => k),
          ),
        ),
      ];
      if (allKeys.length === 0) return null;

      const rows = displayableItems.map((item) =>
        allKeys.map((key) => {
          const value = (item as Record<string, unknown>)[key];
          return value !== undefined ? String(value) : '';
        }),
      );
      return renderTable(allKeys, rows);
    }
  }

  return null;
};

export default NodeTable;
